// Breadcrumbs
import ShopifyVariantSelect from '@/components/ui/ShopifyVariantSelect'
import RetailerSelect from '@/components/ui/RetailerSelect'

// Mantine
import {
    Button,
    ActionIcon,
    Tooltip,
    NumberInput,
    Loader,
    Anchor,
    LoadingOverlay,
} from '@mantine/core'

// React
import { useState, useEffect } from 'react'

// Icons
import { IconExchange, IconTrash, IconPhotoOff } from '@tabler/icons-react'

// Hooks
import useRetailer from '@/hooks/useRetailer'

// Supabase

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Next
import { useNavigate, Link } from 'react-router-dom'

// Lodash
import { sumBy } from 'lodash'

import { getAugmentedVariant } from '@/lib/variant'

// LocalStorage key for draft
const DRAFT_STORAGE_KEY = 'retailer_estimate_draft'

// UI
import Box from '@/components/ui/Box'
import ComponentsAvailabilityChecker from '@/components/ui/ComponentsAvailabilityChecker'
import { RETAILER_ESTIMATES_LOCATION_ID } from '@/lib/constants'

export default function RetailerEstimateCreate() {
    // Lines
    const [lines, setLines] = useState([])
    const [shipping, setShipping] = useState(0)
    const [shippingLoading, setShippingLoading] = useState(false)
    const [isAddingVariant, setIsAddingVariant] = useState(false)
    const [isCreating, setIsCreating] = useState(false)

    // Retailer
    const {
        retailer,
        isLoading: isRetailerLoading,
        clearRetailer,
        onSelect: onRetailerSelect,
    } = useRetailer()

    // API
    const { requestGet, requestPost } = useAxiosApi()

    const copyEstimate = async (estimateId) => {
        const { data } = await requestGet(`/retailer-estimates/${estimateId}`)

        const linesToCopy = data.lines.map((line) => {
            const variant = line.shopify_product_variant

            return {
                product_title: variant.shopify_product.title,
                variant_title: variant.title,
                variant_id: variant.id,
                image: variant.image_url,
                price: line.unit_price,
                sku: variant.sku,
                quantity: line.quantity,
                available_quantity: line.available_quantity,
            }
        })

        setLines(linesToCopy)
    }

    // Clear draft from localStorage
    const clearDraft = () => {
        localStorage.removeItem(DRAFT_STORAGE_KEY)
    }

    // Save draft to localStorage
    const saveDraft = (draftLines, draftShipping, draftRetailerId) => {
        const draft = {
            lines: draftLines,
            shipping: draftShipping,
            retailerId: draftRetailerId,
        }
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
    }

    // Auto-fetch shipping cost when quantities/variants or retailer changes
    // (price changes don't affect shipping so we derive a stable key)
    const shippingKey = lines.map((l) => `${l.variant_id}:${l.quantity}`).join(',')

    useEffect(() => {
        if (!retailer?.id || lines.length === 0) {
            setShipping(0)
            return
        }

        let cancelled = false
        setShippingLoading(true)

        const apiLines = lines.map((l) => ({ variant_id: l.variant_id, quantity: l.quantity }))

        requestPost(`/retailers/${retailer.id}/shipping-cost`, { lines: apiLines })
            .then(({ data }) => {
                if (!cancelled) {
                    setShipping(data.shipping_cost ?? 0)
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setShippingLoading(false) })

        return () => { cancelled = true }
    }, [shippingKey, retailer?.id])

    // Save draft whenever lines, shipping or retailer changes
    useEffect(() => {
        // Only save if we have meaningful data
        if (lines.length > 0 || shipping > 0 || retailer?.id) {
            saveDraft(lines, shipping, retailer?.id)
        }
    }, [lines, shipping, retailer?.id])

    // On first load of the page, check if there is a retailer in the URL or restore draft
    useEffect(() => {
        const url = new URL(window.location.href)
        const retailerId = url.searchParams.get('retailer_id')
        const copyEstimateId = url.searchParams.get('copy_estimate_id')

        // If copying an estimate, don't restore draft
        if (copyEstimateId) {
            copyEstimate(copyEstimateId)
            if (retailerId) {
                onRetailerSelect({ id: parseInt(retailerId) })
            }
            return
        }

        // If retailer_id in URL, use it
        if (retailerId) {
            onRetailerSelect({ id: parseInt(retailerId) })
        }

        // Try to restore draft from localStorage
        try {
            const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY)
            if (savedDraft) {
                const draft = JSON.parse(savedDraft)

                if (draft.lines?.length > 0) {
                    setLines(draft.lines)
                }

                if (draft.shipping > 0) {
                    setShipping(draft.shipping)
                }

                // Restore retailer if not already set via URL
                if (!retailerId && draft.retailerId) {
                    onRetailerSelect({ id: draft.retailerId })
                }
            }
        } catch (error) {
            console.error('Error restoring draft:', error)
        }
    }, [])

    // Add variant to lines

    const getVariant = async (variantId) => {
        const { data } = await requestGet(`/shopify/variants/${variantId}`)

        return data
    }

    const addVariant = async (variantToAdd) => {
        setIsAddingVariant(true)

        const variant = getAugmentedVariant(await getVariant(variantToAdd.id))

        // getAugmentedVariant already worked out stock minus holds per
        // component at location 1; the estimate promises the smallest of them.
        const quantities = variant.components
            .filter((component) => component.isAvailableCategory)
            .map((component) => component.availableAtLocation ?? 0)

        const available_quantity = Math.min(...quantities)

        const priceOfVariant = Math.round(
            (variant.price *
                (100 - (retailer.default_markup_rate ?? 1) * 100)) /
                100
        )

        // Check if variant is already in lines
        const isAlreadyInLines = lines.some(
            (line) => line.variant_id === variant.id
        )

        // If variant is already in lines, increment quantity
        if (isAlreadyInLines) {
            setLines((prev) => {
                const newLines = [...prev]

                const index = newLines.findIndex(
                    (line) => line.variant_id === variant.id
                )

                newLines[index].quantity += 1

                return newLines
            })
        } else {
            setLines((prev) => [
                ...prev,
                {
                    product_id: variant.shopify_product.id,
                    product_title: variant.shopify_product.title,
                    variant_title: variant.title,
                    variant_id: variant.id,
                    quantity_available: variant.quantity_available,
                    image: variant.image_url,
                    price: priceOfVariant,
                    sku: variant.sku,
                    quantity: 1,
                    available_quantity,
                    isB2BDisallowed: variant.isB2BDisallowed,
                },
            ])
        }

        setIsAddingVariant(false)
    }

    // Lines edit
    const editLinePrice = (index, price) => {
        setLines((prev) => {
            const newLines = [...prev]
            newLines[index].price = price

            return newLines
        })
    }

    const editLineQuantity = (index, quantity) => {
        setLines((prev) => {
            const newLines = [...prev]
            newLines[index].quantity = quantity

            return newLines
        })
    }

    const removeLine = (index) => {
        setLines((prev) => {
            const newLines = [...prev]
            newLines.splice(index, 1)

            return newLines
        })
    }

    // Compute total
    const total_without_shipping = lines.reduce((acc, line) => {
        return acc + line.price * line.quantity
    }, 0)

    const total = total_without_shipping + shipping

    // Save estimates in database
    const isValidEstimate =
        retailer && lines.length > 0 && retailer.sellsy_billing_address

    const navigate = useNavigate()

    const createEstimate = async () => {
        if (!isValidEstimate || isCreating) return

        setIsCreating(true)

        try {
            // Create estimate
            const { data } = await requestPost(
                '/retailers/retailer-estimates',
                {
                    retailer,
                    lines,
                    shipping,
                }
            )

            if (data) {
                const id = data.id

                // Clear draft after successful creation
                clearDraft()

                navigate(`/retailer_estimates/${id}`)
            } else {
                setIsCreating(false)
            }
        } catch (error) {
            setIsCreating(false)
        }
    }

    return (
        <div>
            <div className="p-5 flex items-center justify-center">
                <div className="w-full max-w-[900px]">
                    <Box>
                        <LoadingOverlay
                            visible={isRetailerLoading || isAddingVariant}
                        />

                        <span className="text-lg font-medium">
                            Nouveau devis
                        </span>

                        {!retailer && !isRetailerLoading && (
                            <RetailerSelect
                                label="Associer un revendeur"
                                onSelect={onRetailerSelect}
                            />
                        )}

                        {retailer &&
                            !isRetailerLoading &&
                            retailer.sellsy_billing_address && (
                                <div className="flex flex-col gap-5">
                                    <div className="flex flex-col gap-1">
                                        <div className="flex flex-col border p-5 ">
                                            <span className="mb-1 text-sm font-medium">
                                                Adresse de facturation Sellsy
                                            </span>

                                            <span>{retailer.name}</span>

                                            <span>
                                                {
                                                    retailer
                                                        .sellsy_billing_address
                                                        .address_line_1
                                                }
                                            </span>

                                            <span>
                                                {
                                                    retailer
                                                        .sellsy_billing_address
                                                        .address_line_2
                                                }
                                            </span>

                                            <span>
                                                {
                                                    retailer
                                                        .sellsy_billing_address
                                                        .address_line_3
                                                }
                                            </span>

                                            <span>
                                                {
                                                    retailer
                                                        .sellsy_billing_address
                                                        .address_line_4
                                                }
                                            </span>

                                            <span>
                                                {retailer.sellsy_billing_address
                                                    .postal_code +
                                                    ' ' +
                                                    retailer
                                                        .sellsy_billing_address
                                                        .city}
                                            </span>

                                            <span>
                                                {
                                                    retailer
                                                        .sellsy_billing_address
                                                        .country
                                                }
                                            </span>

                                            <Button
                                                className="mt-5 "
                                                variant="default"
                                                onClick={clearRetailer}
                                            >
                                                Changer de revendeur
                                            </Button>
                                        </div>

                                        <div className="flex flex-col gap-3 mt-3">
                                            {lines.length > 0 && (
                                                <div className="flex flex-col border px-5 divide-y">
                                                    {lines.map(
                                                        (line, index) => (
                                                            <div
                                                                key={index}
                                                                className="grid grid-cols-[60px_1fr_300px_60px] items-center gap-5 py-5"
                                                            >
                                                                <div className="aspect-square h-[60px] bg-gray-100 rounded-lg relative">
                                                                    {line.image && (
                                                                        <img
                                                                            src={
                                                                                line.image +
                                                                                '&width=100'
                                                                            }
                                                                            className="w-full h-full p-2 object-contain"
                                                                        />
                                                                    )}

                                                                    {!line.image && (
                                                                        <div className="flex items-center justify-center w-full h-full text-gray-400">
                                                                            <IconPhotoOff size="1.5rem" />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="flex flex-col">
                                                                    <Anchor
                                                                        component={
                                                                            Link
                                                                        }
                                                                        href={`/products/shopify//${line.product_id}`}
                                                                    >
                                                                        {
                                                                            line.product_title
                                                                        }
                                                                    </Anchor>

                                                                    {line.variant_title !==
                                                                        'Default Title' && (
                                                                        <span className="text-gray-500">
                                                                            {
                                                                                line.variant_title
                                                                            }
                                                                        </span>
                                                                    )}

                                                                    <span className="text-gray-400 text-xs mt-1">
                                                                        {
                                                                            line.sku
                                                                        }
                                                                    </span>
                                                                </div>

                                                                <div className="flex gap-5">
                                                                    <NumberInput
                                                                        min={0}
                                                                        clampBehavior="strict"
                                                                        suffix=" €"
                                                                        value={
                                                                            line.price
                                                                        }
                                                                        decimalSeparator=","
                                                                        thousandSeparator=" "
                                                                        onChange={(
                                                                            value
                                                                        ) =>
                                                                            editLinePrice(
                                                                                index,
                                                                                value
                                                                            )
                                                                        }
                                                                    />

                                                                    <div>
                                                                        <NumberInput
                                                                            clampBehavior="strict"
                                                                            min={
                                                                                1
                                                                            }
                                                                            value={
                                                                                line.quantity
                                                                            }
                                                                            prefix="×"
                                                                            w={
                                                                                100
                                                                            }
                                                                            onChange={(
                                                                                value
                                                                            ) =>
                                                                                editLineQuantity(
                                                                                    index,
                                                                                    value
                                                                                )
                                                                            }
                                                                        />

                                                                        {line.isB2BDisallowed && (
                                                                            <span className="text-red-500 text-xs">
                                                                                B2B
                                                                                refusé
                                                                            </span>
                                                                        )}

                                                                        {!line.isB2BDisallowed &&
                                                                            line.available_quantity >
                                                                                0 && (
                                                                                <span className="text-xs text-gray-500">
                                                                                    {
                                                                                        line.available_quantity
                                                                                    }{' '}
                                                                                    disponibles
                                                                                </span>
                                                                            )}

                                                                        {!line.isB2BDisallowed &&
                                                                            line.available_quantity <=
                                                                                0 && (
                                                                                <span className="text-red-500 text-xs">
                                                                                    Indisponible
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                </div>

                                                                <ActionIcon
                                                                    className="ml-auto my-auto"
                                                                    size="lg"
                                                                    variant="default"
                                                                    onClick={() =>
                                                                        removeLine(
                                                                            index
                                                                        )
                                                                    }
                                                                >
                                                                    <IconTrash size="1rem" />
                                                                </ActionIcon>
                                                            </div>
                                                        )
                                                    )}

                                                    <div className="flex justify-between py-5 text-gray-400 text-xs ">
                                                        <span>
                                                            {lines.length}{' '}
                                                            lignes
                                                        </span>

                                                        <span>
                                                            {sumBy(
                                                                lines,
                                                                'quantity'
                                                            ) + ' unités'}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {lines.length === 0 && (
                                                <div className="border flex justify-center p-5 bg-gray-50 text-gray-400 text-sm text-center">
                                                    Aucune ligne
                                                </div>
                                            )}

                                            <ShopifyVariantSelect
                                                label="Ajouter une variante"
                                                onSelect={addVariant}
                                            />

                                            {lines.length > 0 && (
                                                <ComponentsAvailabilityChecker
                                                    locationId={RETAILER_ESTIMATES_LOCATION_ID}
                                                    isSimulation={true}
                                                    mode="available"
                                                    lines={lines.map((line) => ({
                                                        variant_id: line.variant_id,
                                                        quantity: line.quantity,
                                                    }))}
                                                />
                                            )}

                                            <div className="flex flex-col border p-5 ml-auto min-w-[300px] gap-3">
                                                <div className="flex w-full items-center justify-between gap-10">
                                                    <div className="flex items-center gap-2">
                                                        <span>Frais de port</span>
                                                        {shippingLoading && <Loader size="xs" />}
                                                    </div>

                                                    <NumberInput
                                                        w={130}
                                                        min={0}
                                                        clampBehavior="strict"
                                                        suffix=" €"
                                                        decimalSeparator=","
                                                        thousandSeparator=" "
                                                        onChange={(value) => setShipping(value)}
                                                        value={shipping}
                                                        disabled={shippingLoading}
                                                    />
                                                </div>

                                                <div className="flex w-full justify-between gap-10">
                                                    <span>Total HT</span>

                                                    <span className="tabular-nums">
                                                        {Intl.NumberFormat(
                                                            'fr-FR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'EUR',
                                                            }
                                                        ).format(total)}
                                                    </span>
                                                </div>

                                                <span className="text-center text-xs text-gray-500">
                                                    Le total TTC sera calculé
                                                    par Sellsy
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        disabled={!isValidEstimate}
                                        className="mx-auto"
                                        onClick={createEstimate}
                                        loading={isCreating}
                                    >
                                        Créer le devis
                                    </Button>
                                </div>
                            )}
                    </Box>
                </div>
            </div>
        </div>
    )
}
